/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type FormattedContentStackLocationsSEOMetadata } from '@/features/contentstack/interface/models';
import { getLocationsSEOMetadata } from '@/features/contentstack/lib/locations/getLocationsSEOMetadata.server'; // eslint-disable-line no-restricted-imports
import { getAxiosForClient } from '@/lib/axios';
import type { PageContextType } from '@/next-types';
import { type QueryClient, useQuery, type QueryFunctionContext } from '@tanstack/react-query';
import type { LocationsSEOPageType } from '@/features/contentstack/interface';

export type LocationsSEOMetadataOptions = {
  pageType: LocationsSEOPageType;
  state?: string;
  city?: string;
};

const URL = '/api/contentstack/locationsSEOMetadata';

const getContentStackLocationsSEO = async ({
  queryKey,
}: QueryFunctionContext<
  ReturnType<typeof getLocationsSEOKey>
>): Promise<FormattedContentStackLocationsSEOMetadata | null> => {
  const [, , { pageType, state, city }] = queryKey;

  const params: Record<string, string> = {
    pageType,
  };

  if (state) {
    params.state = state;
  }
  if (city) {
    params.city = city;
  }

  const response = await getAxiosForClient().get<FormattedContentStackLocationsSEOMetadata | null>(
    URL,
    { params },
  );
  return response.data;
};

const getLocationsSEOKey = ({ pageType, state, city }: LocationsSEOMetadataOptions) =>
  [
    'cms',
    'locations_seo_metadata',
    {
      pageType,
      state,
      city,
    },
  ] as const;

export const useLocationsSEOMetadata = ({
  pageType,
  state,
  city,
  enabled = true,
}: LocationsSEOMetadataOptions & { enabled?: boolean }) => {
  return useQuery({
    queryKey: getLocationsSEOKey({ pageType, state, city }),
    queryFn: getContentStackLocationsSEO,
    enabled: enabled && !!pageType,
    staleTime: 5 * 60 * 1000, // 5 minutes - SEO content doesn't change frequently
  });
};

export const prefetchLocationsSEOMetadata = async (
  options: LocationsSEOMetadataOptions,
  queryClient: QueryClient,
  context: PageContextType,
) => {
  return queryClient.prefetchQuery({
    queryKey: getLocationsSEOKey(options),
    queryFn: ({ queryKey: [, , queryOptions] }) =>
      getLocationsSEOMetadata(context.req, queryOptions),
  });
};
